import React from "react";
import { motion } from "framer-motion";
import blogImage1 from "../../assets/blogs/model_physique_without_bulking/b1.jpg";
import blogImage2 from "../../assets/blogs/model_physique_without_bulking/b2.jpg";
import RecentPosts from "../../components/blogs/RecentPosts";
import Footer from "../../components/homepage/Footer";

export default function ModelPhysiqueWithoutBulking() {
  return (
    <>
      <div className="flex max-w-[1200px] mx-auto gap-10 py-16 px-4">

        {/* LEFT BLOG CONTENT WITH BLACK PANEL */}
        <motion.div
          className="w-full lg:w-3/4 bg-black/30 p-10 rounded-xl flex flex-col gap-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >

          {/* MAIN IMAGE */}
          <div className="relative w-full h-[420px] md:h-[520px] rounded-xl overflow-hidden shadow-lg">
            <img
              src={blogImage1}
              alt="Model Physique Without Bulking"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 w-full bg-black/30 p-6 text-white">
              <h1 className="text-4xl md:text-5xl font-bold">
                How to Build a Model Physique Without Bulking
              </h1>
              <p className="text-gray-200 text-lg">Kalon | November 26, 2025</p>
            </div>
          </div>

          {/* BLOG CONTENT */}
          <div className="flex flex-col gap-8 text-gray-200 text-lg leading-relaxed">

            <p>
              Achieving a model-like physique without bulking is a common goal for those who want lean definition and symmetry without high mass.
              This method relies on controlled strength work, balanced conditioning, and nutrition that supports steady muscle tone rather than size.
            </p>

            {/* SECTION 1 */}
            <div>
              <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
                1. Emphasize Functional Strength
              </h2>
              <p className="mb-3">
                Functional strength training shapes lean muscle and improves posture while supporting stable joint control. It produces definition
                without the heavy load cycles seen in traditional bulking. Slow tempo movements help activate targeted areas and promote balanced development.
              </p>
              <ul className="list-disc list-inside ml-4">
                <li>Bodyweight movements such as push-ups, pull-ups, dips, and lunges.</li>
                <li>Light to moderate resistance for controlled hypertrophy.</li>
                <li>Slow, steady reps that emphasize muscle activation.</li>
              </ul>
            </div>

           {/* SECTION 2 – TEXT + IMAGE SIDE BY SIDE */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">

  {/* TEXT LEFT */}
  <div>
    <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
      2. Prioritize High-Intensity Interval Training (HIIT)
    </h2>

    <p className="mb-3">
      HIIT supports fat reduction and helps maintain lean tissue. This type of training helps the body use energy in a steady and controlled way. Short bursts of effort keep the muscles active without adding bulk. These sessions help people who aim for a defined shape with a stable output. The quick pace helps balance strength and stamina in a simple format. HIIT also keeps the heart rate in a range that supports proper conditioning. This method suits people who prefer short workouts with clear structure. When paired with steady recovery, HIIT helps keep muscle tone firm and stable through the week.
    </p>

    <ul className="list-disc list-inside ml-4">
      <li>Sprint intervals and full-body circuits.</li>
      <li>Combined cardio and strength efforts in sessions under 30 minutes.</li>
      <li>Two to three HIIT sessions weekly for metabolic support.</li>
    </ul>
  </div>

  {/* IMAGE RIGHT */}
  <img
    src={blogImage2}
    alt="Functional Strength"
    className="rounded-xl w-full object-cover h-[480px] md:h-[520px] shadow-lg"
  />
</div>


            {/* SECTION 3 — NUTRITION TABLE */}
            <div>
              <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
                3. Nutrition for Lean Muscle
              </h2>
              <p>
                Nutrition guides lean muscle support while limiting unnecessary mass gain. Each meal should provide steady energy and balanced macronutrients for daily activity and recovery.
              </p>

              <table className="w-full text-left border border-gray-700 rounded-xl overflow-hidden mt-4">
                <thead className="bg-neonTurquoise/20 text-white">
                  <tr>
                    <th className="px-4 py-2">Meal</th>
                    <th className="px-4 py-2">Protein</th>
                    <th className="px-4 py-2">Carbs</th>
                    <th className="px-4 py-2">Fats</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-900 text-gray-200">
                  <tr className="hover:bg-gray-800 transition">
                    <td className="px-4 py-2">Breakfast</td>
                    <td className="px-4 py-2">Egg whites, yogurt</td>
                    <td className="px-4 py-2">Oats, fruit</td>
                    <td className="px-4 py-2">Avocado, seeds</td>
                  </tr>
                  <tr className="hover:bg-gray-800 transition">
                    <td className="px-4 py-2">Lunch</td>
                    <td className="px-4 py-2">Chicken, turkey</td>
                    <td className="px-4 py-2">Quinoa, rice</td>
                    <td className="px-4 py-2">Nuts, olive oil</td>
                  </tr>
                  <tr className="hover:bg-gray-800 transition">
                    <td className="px-4 py-2">Snack</td>
                    <td className="px-4 py-2">Shake, eggs</td>
                    <td className="px-4 py-2">Fruit</td>
                    <td className="px-4 py-2">Nut butter</td>
                  </tr>
                  <tr className="hover:bg-gray-800 transition">
                    <td className="px-4 py-2">Dinner</td>
                    <td className="px-4 py-2">Fish, lean beef</td>
                    <td className="px-4 py-2">Vegetables</td>
                    <td className="px-4 py-2">Avocado, oils</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* SECTION 4 */}
            <div>
              <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
                4. Recovery & Sleep
              </h2>
              <p className="mb-3">
                Recovery supports muscle tone and structure. Consistent sleep and mobility work maintain balance and prevent fatigue.
              </p>
              <ul className="list-disc list-inside ml-4">
                <li>Light mobility or foam rolling after sessions.</li>
                <li>Stable hydration and limited processed food.</li>
                <li>Seven to nine hours of nightly sleep.</li>
              </ul>
            </div>

            {/* SECTION 5 */}
            <div>
              <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
                5. Weekly Training Structure
              </h2>
              <ol className="list-decimal list-inside ml-4">
                <li>Monday: Upper body strength</li>
                <li>Tuesday: HIIT</li>
                <li>Wednesday: Lower strength + mobility</li>
                <li>Thursday: Light recovery</li>
                <li>Friday: Full-body circuits</li>
                <li>Saturday: Active recovery</li>
                <li>Sunday: Rest</li>
              </ol>
            </div>

            <p>
              With a balanced routine and controlled nutrition, a lean and defined model physique is achievable without the size increase that comes with bulking cycles.
            </p>

          </div>
        </motion.div>

        {/* RIGHT SIDEBAR */}
        <div className="hidden lg:block lg:w-1/4 sticky top-20 self-start">
          <RecentPosts />
        </div>
      </div>

      <Footer />
    </>
  );
}
